import { useState } from "react";

import {
    AppointmentReminder,
    AppointmentSupportedChanges,
    AppointmentTypeFilterOption,
    CancellationOption,
    Deadline,
    RebookingOption,
} from "@accurx/api/appointment";
import { FeatureName, useFeatureFlag } from "@accurx/auth";
import { Ds, ErrorSummary, Feedback, type Option } from "@accurx/design";

import {
    ComposeField,
    PostAppointment,
    SendTimeInput,
    SiteLocationAndSlotType,
} from "../../components";
import { useMessageParts } from "../../hooks/useMessageParts";
import { TemplateType } from "../../types";
import { useFormValidation } from "../../utils";
import { AppointmentChanges } from "../AppointmentChanges/AppointmentChanges";
import { AppointmentTypeFilter, ClinicCodeFilter } from "./components";
import {
    getAppointmentTypeOptions,
    getClinicCodeOptions,
    getExistingCombinations,
    getExistingSiteNamesAndLocationTypes,
    getSiteLocationAndSlotTypeData,
} from "./getFilterOptions";

export type FormValues = {
    selectedSiteNames: string[];
    selectedSlotTypes: Option[];
    selectedAppointmentType: string;
    templateType: TemplateType;
    customMessage: string;
    oneWeekEnabled: boolean;
    threeDaysEnabled: boolean;
    postAppointmentMessageEnabled: boolean;
    postAppointmentMessageBody: string;
    changeDeadline: Deadline;
    cancellationSupport: CancellationOption;
    rebookingSupport: RebookingOption;
};

const defaultInitialValues: FormValues = {
    selectedSiteNames: [],
    selectedSlotTypes: [],
    selectedAppointmentType: "",
    templateType: "FaceToFace",
    customMessage: "",
    oneWeekEnabled: false,
    threeDaysEnabled: false,
    postAppointmentMessageEnabled: false,
    postAppointmentMessageBody: "",
    changeDeadline: Deadline.OneHour,
    cancellationSupport: CancellationOption.Manual,
    rebookingSupport: RebookingOption.NotSupported,
};

export type ReminderFormFieldsProps = {
    workspaceName: string;
    existingReminders: AppointmentReminder[];
    siteNames: string[];
    slotTypes: string[];
    appointmentTypes: AppointmentTypeFilterOption[];
    workspaceHasMultipleSites: boolean;
    initialValues?: Partial<FormValues>;
    submitButtonText: string;
    onSubmit: (formValues: FormValues) => void;
    supportedChanges: AppointmentSupportedChanges;
    // Temporary flag to determine which inputs to render
    availableFilters: "site-name-slot-type" | "clinic-appointment-type";

    cancelButton:
        | {
              show: false;
          }
        | {
              show: true;
              onCancel: () => void;
          };
    deleteButton:
        | {
              show: false;
          }
        | {
              show: true;
              onDelete: () => void;
          };

    isError?: boolean;
    error?: { message: string };
};

export const ReminderFormFields = ({
    workspaceName,
    existingReminders,
    siteNames,
    slotTypes,
    appointmentTypes,
    workspaceHasMultipleSites,
    initialValues,
    submitButtonText,
    onSubmit,
    supportedChanges,
    availableFilters,
    cancelButton,
    deleteButton,
    isError,
    error,
}: ReminderFormFieldsProps) => {
    const safeInitialValues = {
        ...defaultInitialValues,
        ...initialValues,
    };

    const isAppointmentChangesEnabled = useFeatureFlag(
        FeatureName.AppointmentRemindersConfigAmendmentsUIv1,
    );

    const { hasValidationErrors, validationErrors, validate, validatePartial } =
        useFormValidation(availableFilters);
    const existingCombinations = getExistingCombinations(existingReminders);
    const existingSiteNamesAndLocationTypes =
        getExistingSiteNamesAndLocationTypes(existingReminders);

    const [selectedAppointmentType, setSelectedAppointmentType] = useState(
        safeInitialValues.selectedAppointmentType,
    );
    const slotTypeOptions = getClinicCodeOptions(
        slotTypes,
        appointmentTypes,
        existingCombinations,
        selectedAppointmentType,
    );
    const [selectedSlotTypes, setSelectedSlotTypes] = useState<Option[]>(
        safeInitialValues.selectedSlotTypes,
    );
    const [selectedSiteNames, setSelectedSiteNames] = useState<string[]>(
        safeInitialValues.selectedSiteNames,
    );

    const appointmentTypeOptions = getAppointmentTypeOptions(
        slotTypes,
        appointmentTypes,
        existingCombinations,
        selectedSlotTypes,
    );

    const [templateType, setTemplateType] = useState(
        safeInitialValues.templateType,
    );
    const [customMessage, setCustomMessage] = useState(
        safeInitialValues.customMessage,
    );
    const [oneWeekEnabled, setOneWeekEnabled] = useState(
        safeInitialValues.oneWeekEnabled,
    );
    const [threeDaysEnabled, setThreeDaysEnabled] = useState(
        safeInitialValues.threeDaysEnabled,
    );
    const [postAppointmentMessageEnabled, setPostAppointmentMessageEnabled] =
        useState(safeInitialValues.postAppointmentMessageEnabled);
    const [postAppointmentMessageBody, setPostAppointmentMessageBody] =
        useState(safeInitialValues.postAppointmentMessageBody);

    const { messageParts, reminderMessage, postAppointment } = useMessageParts({
        templateType,
        customMessage,
        postAppointmentMessageBody,
        workspaceHasMultipleSites,
        workspaceName,
    });

    const [selectedDeadline, setSelectedDeadline] = useState<
        FormValues["changeDeadline"]
    >(safeInitialValues.changeDeadline);
    const [cancellationSupport, setCancellationSupport] = useState<
        FormValues["cancellationSupport"]
    >(safeInitialValues.cancellationSupport);
    const [rebookingSupport, setRebookingSupport] = useState<
        FormValues["rebookingSupport"]
    >(safeInitialValues.rebookingSupport);

    return (
        <>
            {hasValidationErrors && (
                <ErrorSummary
                    isShown // With this prop set to false an empty div is created in elements tree and flex gap is applied to it
                    title="Please review the errors in the form"
                />
            )}
            <Ds.Card appearance="elevated">
                <Ds.Card.Header px="3" py="2">
                    <Ds.Text as="h2" size="large" weight="bold">
                        Reminder details
                    </Ds.Text>
                </Ds.Card.Header>
                <Ds.Card.Body p="3">
                    <Ds.Box maxWidth="35rem" width="100%">
                        {availableFilters === "site-name-slot-type" && (
                            <SiteLocationAndSlotType
                                allSiteNames={siteNames}
                                allSlotTypes={slotTypes}
                                siteLocationAndSlotTypeData={getSiteLocationAndSlotTypeData(
                                    {
                                        selectedSiteNames,
                                        selectedSlotTypes,
                                        siteNames,
                                        slotTypes,
                                        existingSiteNamesAndLocationTypes,
                                    },
                                )}
                                onUpdateSiteLocationOrSlotTypes={(data) => {
                                    if (data.selectedSiteNames) {
                                        setSelectedSiteNames(
                                            data.selectedSiteNames,
                                        );
                                    }
                                    if (data.selectedSlotTypes) {
                                        setSelectedSlotTypes(
                                            data.selectedSlotTypes.map(
                                                (slotType) => ({
                                                    value: slotType,
                                                    label: slotType,
                                                }),
                                            ),
                                        );
                                    }
                                }}
                                canSiteBeSelected={() => true}
                                siteLocationErrors={
                                    validationErrors.selectedSiteNames ?? []
                                }
                                slotTypeErrors={
                                    validationErrors.selectedSlotTypes ?? []
                                }
                            />
                        )}
                        {availableFilters === "clinic-appointment-type" && (
                            <Ds.Flex gap="3" flexDirection="column">
                                <ClinicCodeFilter
                                    initialOptions={
                                        safeInitialValues.selectedSlotTypes
                                    }
                                    options={slotTypeOptions}
                                    onChange={setSelectedSlotTypes}
                                    errors={validationErrors.selectedSlotTypes}
                                />
                                <AppointmentTypeFilter
                                    initialOption={appointmentTypeOptions.find(
                                        (option) =>
                                            option.value ===
                                            safeInitialValues.selectedAppointmentType,
                                    )}
                                    options={appointmentTypeOptions}
                                    onChange={(selected) => {
                                        setSelectedAppointmentType(
                                            selected.value,
                                        );
                                    }}
                                    errors={
                                        validationErrors.selectedAppointmentType
                                    }
                                />
                            </Ds.Flex>
                        )}
                    </Ds.Box>
                    <Ds.Box my="3">
                        <Ds.Fieldset>
                            <Ds.Legend>Template type</Ds.Legend>
                            <Ds.RadioGroup
                                name="template-type"
                                defaultValue={templateType}
                                onValueChange={(value) => {
                                    setTemplateType(value as TemplateType);
                                }}
                            >
                                <Ds.Flex gap="1.5">
                                    <Ds.RadioGroup.Item value="FaceToFace">
                                        <Ds.RadioGroup.Indicator />
                                        <Ds.RadioGroup.Label>
                                            Face-to-face
                                        </Ds.RadioGroup.Label>
                                    </Ds.RadioGroup.Item>
                                    <Ds.RadioGroup.Item value="Telephone">
                                        <Ds.RadioGroup.Indicator />
                                        <Ds.RadioGroup.Label>
                                            Telephone
                                        </Ds.RadioGroup.Label>
                                    </Ds.RadioGroup.Item>
                                </Ds.Flex>
                            </Ds.RadioGroup>
                        </Ds.Fieldset>
                    </Ds.Box>
                    <ComposeField
                        messageParts={messageParts}
                        charAndFragmentInfo={
                            reminderMessage.charAndFragmentInfo
                        }
                        customMessage={customMessage}
                        onCustomMessageChange={(e) => {
                            const newMessage = e.target.value;
                            setCustomMessage(newMessage);
                            validatePartial({
                                reminderMessageCharacterCount:
                                    reminderMessage.getFullMessageLength(
                                        newMessage,
                                    ),
                            });
                        }}
                        validationErrors={
                            validationErrors.reminderMessageCharacterCount
                        }
                    />
                </Ds.Card.Body>
            </Ds.Card>

            <SendTimeInput
                threeWorkingDayReminderEnabled={threeDaysEnabled}
                toggleThreeDaysReminderEnabled={(newValue) => {
                    setThreeDaysEnabled(newValue);
                    if (isAppointmentChangesEnabled) {
                        validatePartial({
                            changeDeadline: selectedDeadline,
                            threeDaysEnabled: newValue,
                            oneWeekEnabled,
                        });
                    }
                }}
                oneWeekReminderEnabled={oneWeekEnabled}
                toggleOneWeekReminderEnabled={(newValue) => {
                    setOneWeekEnabled(newValue);
                    if (isAppointmentChangesEnabled) {
                        validatePartial({
                            changeDeadline: selectedDeadline,
                            threeDaysEnabled,
                            oneWeekEnabled: newValue,
                        });
                    }
                }}
                changeDeadline={selectedDeadline}
            />

            {isAppointmentChangesEnabled && (
                <AppointmentChanges
                    changeDeadline={selectedDeadline}
                    rebookingSupport={rebookingSupport}
                    cancellationSupport={cancellationSupport}
                    onDeadlineChange={(newChangeDeadline) => {
                        setSelectedDeadline(newChangeDeadline);
                        validatePartial({
                            changeDeadline: newChangeDeadline,
                            threeDaysEnabled,
                            oneWeekEnabled,
                        });
                    }}
                    onCancellationSupportChange={(newCancellationSupport) =>
                        setCancellationSupport(newCancellationSupport)
                    }
                    onRebookingSupportChange={(newRebookingSupport) =>
                        setRebookingSupport(newRebookingSupport)
                    }
                    is1WayOrg={
                        supportedChanges.cancellation ===
                            CancellationOption.Manual &&
                        supportedChanges.rebooking === RebookingOption.Manual
                    }
                    errors={validationErrors.changeDeadline}
                />
            )}

            <PostAppointment
                formData={{
                    postAppointmentCharCount: postAppointment.characterCount,
                    postAppointmentMessageBody,
                    postAppointmentMessageEnabled,
                }}
                validationErrors={[
                    // When we validate post-appointment message we check that
                    // the message body (excluding greeting and signature) is not an empty string
                    // and at the same time combined length of the post-appointment message
                    // should be less than two SMS fragments
                    ...(validationErrors.postAppointmentMessageBody || []),
                    ...(validationErrors.postAppointmentMessageCharacterCount ||
                        []),
                ]}
                onToggle={() =>
                    setPostAppointmentMessageEnabled((prevValue) => !prevValue)
                }
                onMessageInput={({ target: { value } }) => {
                    setPostAppointmentMessageBody(value);
                    validatePartial({
                        postAppointmentMessageCharacterCount:
                            postAppointment.getFullMessageLength(value),
                        postAppointmentMessageBody: value,
                    });
                }}
                messageParts={messageParts}
                charAndFragmentInfo={postAppointment.charAndFragmentInfo}
                spacing={3}
            />

            {isError && error && (
                <Feedback colour="error" title={error.message} />
            )}

            <Ds.Flex
                justifyContent={
                    deleteButton.show ? "space-between" : "flex-end"
                }
                gap="2"
            >
                {deleteButton.show && (
                    <Ds.Button
                        type="button"
                        appearance="danger"
                        onClick={deleteButton.onDelete}
                    >
                        Delete reminder
                    </Ds.Button>
                )}

                <Ds.Flex justifyContent="flex-end" gap="2">
                    {cancelButton.show && (
                        <Ds.Button
                            type="button"
                            appearance="secondary"
                            onClick={cancelButton.onCancel}
                        >
                            Cancel
                        </Ds.Button>
                    )}

                    <Ds.Button
                        type="submit"
                        onClick={(e) => {
                            e.preventDefault();

                            const formValues = {
                                selectedSiteNames,
                                selectedSlotTypes,
                                selectedAppointmentType,
                                templateType,
                                customMessage,
                                reminderMessageCharacterCount:
                                    reminderMessage.characterCount,
                                threeDaysEnabled,
                                oneWeekEnabled,
                                postAppointmentMessageEnabled,
                                postAppointmentMessageBody,
                                postAppointmentMessageCharacterCount:
                                    postAppointment.characterCount,
                                changeDeadline: selectedDeadline,
                                cancellationSupport: cancellationSupport,
                                rebookingSupport: rebookingSupport,
                            };

                            const isValid = validate(formValues).success;

                            if (isValid) {
                                onSubmit(formValues);
                            }
                        }}
                    >
                        {submitButtonText}
                    </Ds.Button>
                </Ds.Flex>
            </Ds.Flex>
        </>
    );
};
